import { useState, useEffect, useRef } from 'react'
import { Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import type { DisputeResponseFormData, DisputeAction } from '../types'

interface DisputeResponseFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: DisputeResponseFormData) => Promise<void> | void
  onSuccess?: () => void
  /** Dispute reason label displayed as context, e.g. "Product not received" */
  disputeReason?: string
  /** ISO due date string for the response deadline */
  responseDueBy?: string
}

type AcceptanceReason = { value: string; label: string }

const ACCEPTANCE_REASONS: AcceptanceReason[] = [
  { value: 'policy_violation', label: 'Policy violation' },
  { value: 'fraudulent_order', label: 'Fraudulent order confirmed' },
  { value: 'goods_not_delivered', label: 'Goods not delivered' },
  { value: 'other', label: 'Other' },
]

const AUTO_CLOSE_MS = 1500

const ACTION_OPTIONS: Array<{ value: DisputeAction; title: string; description: string }> = [
  {
    value: 'submit_evidence',
    title: 'Submit evidence',
    description: 'Provide documentation to challenge the dispute',
  },
  {
    value: 'accept_dispute',
    title: 'Accept dispute',
    description: 'Acknowledge the dispute and allow refund to proceed',
  },
]

export function DisputeResponseForm({
  open,
  onClose,
  onSubmit,
  onSuccess,
  disputeReason,
  responseDueBy,
}: DisputeResponseFormProps) {
  const [action, setAction]                     = useState<DisputeAction>('submit_evidence')
  const [evidenceDescription, setEvidenceDesc]  = useState('')
  const [acceptanceReason, setAcceptanceReason] = useState('')
  const [notes, setNotes]                       = useState('')
  const [isLoading, setIsLoading]               = useState(false)
  const [status, setStatus]                     = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setAction('submit_evidence')
      setEvidenceDesc('')
      setAcceptanceReason('')
      setNotes('')
      setIsLoading(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open])

  const isValid =
    action === 'submit_evidence'
      ? evidenceDescription.trim().length > 0
      : acceptanceReason.length > 0

  const handleSubmit = async () => {
    if (!isValid || isLoading) return
    setIsLoading(true)
    try {
      await onSubmit({
        action,
        evidenceDescription: action === 'submit_evidence' ? evidenceDescription.trim() : undefined,
        acceptanceReason: action === 'accept_dispute' ? acceptanceReason : undefined,
        notes: notes.trim(),
      })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onSuccess?.()
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-[480px] gap-0 p-0 overflow-hidden">
        <DialogResultTransition
          status={status}
          successMessage={action === 'submit_evidence' ? 'Evidence submitted' : 'Dispute accepted'}
          errorMessage="Failed to submit response"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-[17px] font-semibold leading-tight">
            Respond to dispute
          </DialogTitle>
        </DialogHeader>

        {/* ── Scrollable body ── */}
        <div className="px-6 pt-4 pb-6 space-y-5 overflow-y-auto max-h-[calc(100vh-160px)]">

          {/* Info banner */}
          <div className="flex gap-3 rounded-xl bg-[#EFF6FF] p-3.5">
            <Info className="h-4 w-4 text-[#3B82F6] mt-0.5 shrink-0" />
            <div className="text-[13px] leading-[1.45] text-[#1E40AF]">
              {disputeReason && (
                <p className="font-medium mb-0.5">Reason: {disputeReason}</p>
              )}
              <p>
                {responseDueBy
                  ? `Response required by ${responseDueBy}. `
                  : 'A timely response is required. '}
                Choose to submit evidence or accept the dispute.
              </p>
            </div>
          </div>

          {/* Action selector */}
          <div className="space-y-2">
            <label className="text-[13px] font-medium text-foreground">
              How would you like to respond?
            </label>
            <div className="rounded-xl border border-input overflow-hidden divide-y divide-input">
              {ACTION_OPTIONS.map(({ value, title, description }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAction(value)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors',
                    action === value ? 'bg-card' : 'bg-card hover:bg-muted/30',
                  )}
                  aria-pressed={action === value}
                >
                  <span
                    className={cn(
                      'h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors',
                      action === value ? 'border-foreground' : 'border-muted-foreground/40',
                    )}
                  >
                    {action === value && (
                      <span className="h-2 w-2 rounded-full bg-foreground" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-[14px] font-medium text-foreground">{title}</span>
                    <p className="text-[12px] text-muted-foreground mt-0.5">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Conditional: evidence textarea */}
          {action === 'submit_evidence' && (
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-foreground">
                Evidence description
              </label>
              <textarea
                value={evidenceDescription}
                onChange={(e) => setEvidenceDesc(e.target.value)}
                placeholder="Describe the evidence you are providing (tracking info, receipts, correspondence, etc.)..."
                rows={4}
                className="w-full rounded-xl border border-input bg-card px-3.5 py-3 text-[14px] resize-none outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
                aria-label="Evidence description"
              />
              {/* Placeholder for file upload — future enhancement */}
              <div className="rounded-xl border-2 border-dashed border-input p-4 text-center">
                <p className="text-[13px] text-muted-foreground">
                  Attach supporting documents (coming soon)
                </p>
              </div>
            </div>
          )}

          {/* Conditional: acceptance reason chips */}
          {action === 'accept_dispute' && (
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-foreground">
                Reason for accepting
              </label>
              <div className="flex flex-wrap gap-2">
                {ACCEPTANCE_REASONS.map((reason) => {
                  const isSelected = acceptanceReason === reason.value
                  return (
                    <button
                      key={reason.value}
                      type="button"
                      onClick={() => setAcceptanceReason(reason.value)}
                      className={cn(
                        'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-medium border transition-colors',
                        isSelected
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-card text-foreground border-input hover:border-foreground/40',
                      )}
                      aria-pressed={isSelected}
                    >
                      {reason.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Notes (optional) */}
          <div className="space-y-2">
            <label className="text-[13px] font-medium text-foreground">
              Notes{' '}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional context..."
              rows={2}
              className="w-full rounded-xl border border-input bg-card px-3.5 py-3 text-[14px] resize-none outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
              aria-label="Additional notes"
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4 bg-card">
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-5"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="action"
            className="rounded-full px-5"
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
          >
            {isLoading
              ? 'Submitting...'
              : action === 'submit_evidence'
              ? 'Submit evidence'
              : 'Accept dispute'}
          </Button>
        </div>

        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
